import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $cardLimitStore, $cardStore, $profileFeatureStore } from '@lib/store';
import { useCardFlow, useCurrencySymbol, useRouteHook } from '@shared/hooks';
import { cn, PaletteUtils, RoutePaths } from '@shared/utils';
import { AccountInfo, AccountInfoRow, Users, CardSidebar, TransactionsTable, View, PageContainer } from '@widgets';

export const CardPage = () => {
  const params = useParams<{ id: string }>();
  const { navigate } = useRouteHook();
  const id = useMemo(() => (params.id ? parseInt(params.id) : undefined), [params]);

  const profileFeatureStore = useUnit($profileFeatureStore.store);
  const cardStore = useUnit($cardStore.store);

  const { t } = useTranslation('card');

  useEffect(() => {
    if (!id) return;
    $cardStore.events.getCard({ id });
    return () => $cardStore.events.clear();
  }, [id]);

  useEffect(() => {
    if (cardStore.loading) return;
    if (cardStore.error !== 'Action is forbidden.') return;
    $cardStore.events.clear();
    navigate(RoutePaths.error.onNavigate(), { replace: true });
  }, [cardStore.loading, cardStore.error]);

  useEffect(() => {
    if (!cardStore.data || !profileFeatureStore.data?.cardFlows?.find((x) => x.cardId === id)) return;
    $cardLimitStore.events.getLimit({ id: id!, cardKind: cardStore.data.cardKind });
    return () => $cardLimitStore.events.clear();
  }, [cardStore.data?.cardId]);

  const cardFlow = useCardFlow(profileFeatureStore.data?.cardFlows, id);
  const symbol = useCurrencySymbol(cardStore.data?.currency);
  return (
    <PageContainer title={t('title')} back backText={t('backText')} return>
      <View
        loading={cardStore.loading}
        error={cardStore.error}
        onRefresh={() => {
          $cardStore.events.getCard({ id: id! });
          if (!cardStore.data || !profileFeatureStore.data?.cardFlows?.find((x) => x.cardId === id)) return;
          $cardLimitStore.events.getLimit({ id: id!, cardKind: cardStore.data.cardKind });
        }}
        noData={!cardStore.data}
      >
        {cardStore.data && (
          <div className={cn('flex', PaletteUtils.isVcfcPalette && 'max-md:block')}>
            <div
              className={cn(
                'flex h-fit flex-auto flex-col',
                PaletteUtils.isVcfcPalette && 'rounded-lg border border-gray-black bg-white dark:bg-soft-black',
              )}
            >
              <div className={cn(PaletteUtils.isInsigniaPalette && 'mb-12')}>
                <AccountInfo
                  title={cardStore.data.cardLabel ?? ''}
                  type1={cardStore.data.productDisplayName}
                  user={cardStore.data.cardHolder}
                  status={cardStore.data.status}
                  type2={t('accountInfo.fields.type2')}
                  icon={symbol}
                  card={cardStore.data}
                >
                  <AccountInfoRow divider />
                  <AccountInfoRow text={t('accountInfo.fields.cardNumber')} number={cardStore.data.cardNumber} />
                  <AccountInfoRow text={t('accountInfo.fields.accountNumber')} number={cardStore.data.accountNumber} />
                </AccountInfo>
              </div>
              {profileFeatureStore.data?.userManagement?.managerAccountsOrCardsPermissions && id && <Users cardId={id!} />}
              {cardFlow?.viewTransactions && <TransactionsTable cardId={cardStore.data.cardId} />}
            </div>
            <div className={cn('ml-8 w-80 flex-none', PaletteUtils.isVcfcPalette && 'max-md:ml-0 max-md:w-full')}>
              <CardSidebar card={cardStore.data} />
            </div>
          </div>
        )}
      </View>
    </PageContainer>
  );
};
